import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Page, PageHeader } from '../components/Page';
import { PCard } from '../components/PCard';
import { StatCard } from '../components/StatCard';
import { StatusPill } from '../components/StatusPill';
import { Async } from '../components/Async';
import { InvoiceTable } from '../components/InvoiceTable';
import { IcFile, IcPill, IcCreditCard } from '../components/icons';
import { useFetch } from '../lib/useFetch';
import { useAuth } from '../lib/auth';
import { getClient, getInvoices, createCardLink, generateClientDraft, draftResultMessage, updateClientStatus, updateClientRecipients, updateClientEmailFlags, ApiError, type PaymentRail, type CardOverviewRail, type ClientEmailFlags } from '../lib/api';
import { fmtCents, fmtCents0, fmtNum } from '../lib/format';

const railLabel: Record<PaymentRail, string> = { stripe: 'Platform (Stripe)', hyperswitch: 'Pharmacy (Hyperswitch)' };

function CardLine({ r }: { r: CardOverviewRail }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '4px 0' }}>
      <span className="muted">{railLabel[r.rail]}</span>
      {r.selected ? (
        <span>{(r.selected.brand ?? 'card').toString().toUpperCase()} •••• {r.selected.last4}{r.cardCount > 1 ? ` +${r.cardCount - 1}` : ''}</span>
      ) : (
        <StatusPill tone="error">No card</StatusPill>
      )}
    </div>
  );
}

export const ClientDetailPage = () => {
  const { id } = useParams();
  const { can } = useAuth();
  const detail = useFetch(() => getClient(id!), [id]);
  const invoices = useFetch(() => getInvoices({ clientId: id }), [id]);
  const [linkMsg, setLinkMsg] = useState<string | null>(null);
  const [draftMsg, setDraftMsg] = useState<string | null>(null);
  const [drafting, setDrafting] = useState(false);
  const [draftOpen, setDraftOpen] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);

  async function changeStatus(next: 'active' | 'paused' | 'churned') {
    // Going active starts real billing on the next run — confirm before flipping.
    if (next === 'active' && !window.confirm('Set this client active? It will be billed on the next run.')) return;
    setSavingStatus(true);
    try {
      await updateClientStatus(id!, next);
      detail.reload();
    } catch (e) {
      window.alert(e instanceof ApiError ? e.message : 'Could not update status');
    } finally {
      setSavingStatus(false);
    }
  }

  async function copyLink() {
    try {
      const { token } = await createCardLink(id!);
      await navigator.clipboard.writeText(`${window.location.origin}/add-card/${token}`);
      setLinkMsg('Add-card link copied.');
    } catch (e) {
      setLinkMsg(e instanceof ApiError ? e.message : 'Could not create link');
    }
  }

  const DAY_MS = 24 * 60 * 60 * 1000;
  const MAX_LOOKBACK = 366; // server clamps to this; the practical "all orders" reach.
  /** Translate the optional From (lower bound) / Through (asOf) inputs into the
   *  API's asOf + lookbackDays.
   *   - Through only     → all uninvoiced orders on or before that date (max reach).
   *   - Through + From   → just that bounded window.
   *   - From only        → from that date through today.
   *   - Neither          → the server's default 90-day window ending today. */
  function rangeBody(): { asOf?: string; lookbackDays?: number } | string {
    const body: { asOf?: string; lookbackDays?: number } = {};
    if (toDate) body.asOf = toDate;
    if (fromDate) {
      const from = new Date(fromDate).getTime();
      const to = new Date(toDate || new Date().toISOString().slice(0, 10)).getTime();
      if (Number.isNaN(from) || Number.isNaN(to)) return 'Enter valid dates.';
      if (from > to) return 'From date must be on or before the Through date.';
      body.lookbackDays = Math.max(1, Math.ceil((to - from) / DAY_MS));
    } else if (toDate) {
      // "All orders through X" — reach as far back as the server allows.
      body.lookbackDays = MAX_LOOKBACK;
    }
    return body;
  }

  async function generateDraft() {
    const range = rangeBody();
    if (typeof range === 'string') {
      setDraftMsg(range);
      return;
    }
    const openDrafts = (invoices.data?.invoices ?? []).filter((i) => i.status === 'draft').length;
    let supersede = false;
    if (openDrafts > 0) {
      const noun = openDrafts > 1 ? `${openDrafts} open drafts` : 'an open draft';
      if (!window.confirm(`This client already has ${noun}. Void ${openDrafts > 1 ? 'them' : 'it'} and generate a fresh draft?`)) return;
      supersede = true;
    }
    setDrafting(true);
    setDraftMsg(null);
    try {
      const r = await generateClientDraft(id!, { supersede, ...range });
      setDraftMsg(draftResultMessage(r));
      setDraftOpen(false);
      invoices.reload();
      detail.reload();
    } catch (e) {
      setDraftMsg(e instanceof ApiError ? e.message : 'Could not generate draft');
    } finally {
      setDrafting(false);
    }
  }

  return (
    <Page>
      <Async state={detail} isEmpty={() => false} emptyLabel="">
        {(d) => {
          const c = d.client;
          const pricing = c.pharmacyFlatCents != null ? `Flat ${fmtCents(c.pharmacyFlatCents)}/script` : 'Per-drug pricing';
          return (
            <>
              <PageHeader
                title={c.name}
                sub={<Link to="/clients" style={{ color: 'var(--p-muted-fg)', fontSize: 13 }}>← All clients</Link>}
                actions={
                  <>
                    <StatusPill tone={c.type === 'telemed' ? 'in-progress' : 'neutral'}>{c.type === 'telemed' ? 'Telemed' : 'Express'}</StatusPill>
                    {can('clients:update') ? (
                      <select
                        className="select"
                        value={c.status}
                        disabled={savingStatus}
                        onChange={(e) => changeStatus(e.target.value as 'active' | 'paused' | 'churned')}
                        title="Client status — only active clients are billed"
                        style={{ width: 'auto' }}
                      >
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="churned">Churned</option>
                      </select>
                    ) : (
                      <StatusPill tone={c.status === 'active' ? 'success' : 'neutral'}>{c.status}</StatusPill>
                    )}
                    {can('billing:run') && (
                      <button className="btn" onClick={() => { setDraftMsg(null); setDraftOpen(true); }} disabled={drafting}>
                        Generate draft…
                      </button>
                    )}
                  </>
                }
              />

              {draftOpen && can('billing:run') && (
                <DraftModal
                  clientName={c.name}
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                  drafting={drafting}
                  error={draftMsg}
                  onCancel={() => setDraftOpen(false)}
                  onGenerate={generateDraft}
                />
              )}

              {draftMsg && (
                <div style={{ padding: '8px 12px', borderRadius: 6, background: 'rgba(16,185,129,.08)', color: 'var(--status-success-ink,#047857)', fontSize: 13 }}>
                  {draftMsg}
                </div>
              )}

              <div className="grid grid-3">
                <StatCard tone="staged" icon={IcFile} label="Outstanding" value={fmtCents0(d.outstandingCents)} sub={`${d.openInvoiceCount} open`} />
                <StatCard tone="in-transit" icon={IcPill} label="Unbilled (accrued)" value={fmtCents0(d.accruedCents)} sub={d.accrued ? `pharm ${fmtCents0(d.accrued.pharmacyCents)} · ship ${fmtCents0(d.accrued.shippingCents)}` : 'not yet invoiced'} />
                <StatCard tone="in-progress" icon={IcCreditCard} label="Total exposure" value={fmtCents0(d.outstandingCents + d.accruedCents)} />
              </div>

              <div className="grid grid-2">
                <div>
                  <div className="section-title">Configuration</div>
                  <PCard style={{ padding: 16, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <Row label="Pharmacy pricing" value={<Link to="/pricing">{pricing}</Link>} />
                    <Row label="Payment terms" value={`Net ${c.pharmacyTermsDays} days · charge ${c.pharmacyChargeOnDueDate ? 'on due date' : 'Monday'}`} />
                    <Row label="Pharmacy codes" value={c.pharmacyIds.join(', ') || '—'} />
                    {c.type === 'express' && <Row label="Core account" value={c.coreAccountId || '—'} />}
                    {c.platformPlan && <Row label="Platform plan" value={`${fmtCents0(c.platformPlan.baseFeeCents)} base · ${fmtNum(c.platformPlan.minPatients)} floor · ${fmtCents(c.platformPlan.perPatientCents)}/patient`} />}
                  </PCard>
                </div>
                <div>
                  <div className="section-title">Cards on file</div>
                  <PCard style={{ padding: 16 }}>
                    {d.cards.map((r) => <CardLine key={r.rail} r={r} />)}
                    {can('payments:manage') && (
                      <button className="btn" style={{ marginTop: 10 }} onClick={copyLink}>Copy add-card link</button>
                    )}
                    {linkMsg && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--status-success-ink,#047857)' }}>{linkMsg}</div>}
                  </PCard>
                </div>
              </div>

              <div>
                <div className="section-title">Invoice recipients</div>
                <RecipientsEditor
                  clientId={id!}
                  initial={c.invoiceRecipients}
                  canEdit={can('clients:update')}
                />
                <EmailFlagsEditor
                  clientId={id!}
                  initialAggressiveReminders={c.aggressiveReminders}
                  initialAttachedDetail={c.attachedDetail}
                  hasRecipients={c.invoiceRecipients.length > 0}
                  canEdit={can('clients:update')}
                />
              </div>

              <div>
                <div className="section-title">Invoices</div>
                <Async state={invoices} isEmpty={(x) => x.invoices.length === 0} emptyLabel="No invoices yet.">
                  {({ invoices: rows }) => (
                    <InvoiceTable
                      invoices={rows}
                      backTo={{ to: `/clients/${id}`, label: `Back to ${c.name}` }}
                      showClient={false}
                    />
                  )}
                </Async>
              </div>
            </>
          );
        }}
      </Async>
    </Page>
  );
};

const Row = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
    <span className="muted">{label}</span>
    <span style={{ textAlign: 'right' }}>{value}</span>
  </div>
);

function RecipientsEditor({ clientId, initial, canEdit }: { clientId: string; initial: string[]; canEdit: boolean }) {
  const [emails, setEmails] = useState<string[]>(initial);
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function save(next: string[]) {
    setSaving(true);
    setErr(null);
    try {
      const r = await updateClientRecipients(clientId, next);
      setEmails(r.invoiceRecipients);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not save recipients');
    } finally {
      setSaving(false);
    }
  }

  function add() {
    const email = input.trim();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setErr('Enter a valid email address.');
      return;
    }
    if (emails.some((e) => e.toLowerCase() === email.toLowerCase())) {
      setErr('That email is already on the list.');
      return;
    }
    setInput('');
    void save([...emails, email]);
  }

  return (
    <PCard style={{ padding: 16, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="muted">
        These emails receive this client's pharmacy/shipping invoice (PDF + link) when it's issued.
        {emails.length === 0 && ' No one is notified yet.'}
      </div>
      {emails.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {emails.map((e) => (
            <span key={e} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', borderRadius: 999, background: 'var(--p-muted-bg,#f1f5f9)' }}>
              {e}
              {canEdit && (
                <button
                  onClick={() => void save(emails.filter((x) => x !== e))}
                  disabled={saving}
                  title="Remove"
                  style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--p-muted-fg)', fontSize: 14, lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="email"
            className="input"
            placeholder="name@company.com"
            value={input}
            onChange={(ev) => setInput(ev.target.value)}
            onKeyDown={(ev) => { if (ev.key === 'Enter') { ev.preventDefault(); add(); } }}
            disabled={saving}
            style={{ maxWidth: 280 }}
          />
          <button className="btn btn--ghost" onClick={add} disabled={saving || !input.trim()}>Add</button>
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--status-error-ink,#b91c1c)' }}>{err}</div>}
    </PCard>
  );
}

/** Per-client email feature flags. Both act on the recipient list above. */
function EmailFlagsEditor({
  clientId,
  initialAggressiveReminders,
  initialAttachedDetail,
  hasRecipients,
  canEdit,
}: {
  clientId: string;
  initialAggressiveReminders: boolean;
  initialAttachedDetail: boolean;
  hasRecipients: boolean;
  canEdit: boolean;
}) {
  const [flags, setFlags] = useState<ClientEmailFlags>({
    aggressiveReminders: initialAggressiveReminders,
    attachedDetail: initialAttachedDetail,
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function toggle(key: keyof ClientEmailFlags, value: boolean) {
    const previous = flags;
    setFlags({ ...flags, [key]: value }); // optimistic — reverted on failure
    setSaving(true);
    setErr(null);
    try {
      const r = await updateClientEmailFlags(clientId, { [key]: value });
      setFlags({ aggressiveReminders: r.aggressiveReminders, attachedDetail: r.attachedDetail });
    } catch (e) {
      setFlags(previous);
      setErr(e instanceof ApiError ? e.message : 'Could not save settings');
    } finally {
      setSaving(false);
    }
  }

  const rows: { key: keyof ClientEmailFlags; label: string; help: string }[] = [
    {
      key: 'aggressiveReminders',
      label: 'Aggressive reminders',
      help: 'Email the recipients above every day with a summary of every invoice past its due date. Stops on its own once nothing is overdue.',
    },
    {
      key: 'attachedDetail',
      label: 'Attach supporting detail',
      help: "Attach the supporting CSV (order ids, tracking numbers, products) to this client's invoice emails and overdue reminders.",
    },
  ];

  return (
    <PCard style={{ padding: 16, marginTop: 12, fontSize: 13, display: 'flex', flexDirection: 'column', gap: 12 }}>
      {rows.map((row) => (
        <div key={row.key} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <input
            id={`flag-${row.key}`}
            type="checkbox"
            checked={flags[row.key]}
            disabled={!canEdit || saving}
            onChange={(ev) => void toggle(row.key, ev.target.checked)}
            style={{ marginTop: 2 }}
          />
          <label htmlFor={`flag-${row.key}`} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
            <div style={{ fontWeight: 500 }}>{row.label}</div>
            <div className="muted" style={{ fontSize: 12 }}>{row.help}</div>
          </label>
        </div>
      ))}
      {(flags.aggressiveReminders || flags.attachedDetail) && !hasRecipients && (
        <div className="muted" style={{ fontSize: 12 }}>
          No invoice recipients yet — add one above or these emails have nowhere to go.
        </div>
      )}
      {err && <div style={{ fontSize: 12, color: 'var(--status-error-ink,#b91c1c)' }}>{err}</div>}
    </PCard>
  );
}

function DraftModal({
  clientName,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
  drafting,
  error,
  onCancel,
  onGenerate,
}: {
  clientName: string;
  fromDate: string;
  toDate: string;
  setFromDate: (v: string) => void;
  setToDate: (v: string) => void;
  drafting: boolean;
  error: string | null;
  onCancel: () => void;
  onGenerate: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(15,23,42,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        className="p-card"
        onClick={(e) => e.stopPropagation()}
        style={{ padding: 20, width: 'min(440px, 100%)', display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Generate draft invoice</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{clientName}</div>
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <span className="muted" style={{ fontSize: 13 }}>Through date</span>
            <input type="date" className="input" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
            <span className="muted" style={{ fontSize: 13 }}>From (optional)</span>
            <input type="date" className="input" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </label>
        </div>

        <p className="muted" style={{ fontSize: 12, margin: 0 }}>
          Bills uninvoiced orders on or before the Through date (up to 1 year back). Add a From date to bound the start. Leave both blank for the default 90-day window ending today. Pricing is applied at current rates.
        </p>

        {error && (
          <div style={{ fontSize: 13, color: 'var(--status-error-ink,#b91c1c)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn--ghost" onClick={onCancel} disabled={drafting}>Cancel</button>
          <button className="btn btn--primary" onClick={onGenerate} disabled={drafting}>
            {drafting ? 'Generating…' : 'Generate'}
          </button>
        </div>
      </div>
    </div>
  );
}
